import { Controller } from "@hotwired/stimulus"

// BD payment Stripe integration — Stripe deferred-intent flow:
// - Element initialized with mode='subscription' + amount, no client_secret.
// - PATCH updates session totals only (fast, no Stripe round-trip).
// - On submit: elements.submit() validates the card, /payment/prepare creates
//   the Stripe Subscription with the freshest totals, confirmPayment runs
//   against the new client_secret.
// - amountSync target's data-amount-cents is the source of truth; whenever
//   it changes (via turbo_stream.replace), elements.update({ amount }) syncs
//   the Element so dynamic methods (e.g. Klarna/Afterpay) see the right total.
export default class extends Controller {
  static targets = ["paymentMount", "submitBtn", "errorMessage", "amountSync", "overlay"]
  static values = {
    stripePublicKey: String,
    amount: Number,
    currency: { type: String, default: "eur" },
    prepareUrl: String,
    returnUrl: String
  }

  connect() {
    if (typeof Stripe === "undefined") {
      this.showError("Stripe.js failed to load. Please refresh the page.")
      return
    }
    this.stripe = Stripe(this.stripePublicKeyValue)
    this.mountElements()
    // Observer setup is driven by amountSyncTargetConnected — Stimulus calls
    // it on initial mount as well as after each turbo_stream.replace.
  }

  disconnect() {
    if (this.paymentElement && this._onStripeChange) {
      this.paymentElement.off("change", this._onStripeChange)
    }
    this.paymentElement = null
    this._onStripeChange = null
    this.disconnectObserver()
    this.elements = null
  }

  mountElements() {
    if (!this.hasPaymentMountTarget) return
    this._stripeComplete = false
    this.elements = this.stripe.elements({
      mode: "subscription",
      amount: this.amountValue,
      currency: this.currencyValue,
      paymentMethodCreation: "manual",
      appearance: this.appearance()
    })
    this.paymentElement = this.elements.create("payment", { layout: "tabs" })
    this.paymentElement.mount(this.paymentMountTarget)
    this._onStripeChange = (event) => {
      this._stripeComplete = Boolean(event.complete)
      this.refreshCta()
    }
    this.paymentElement.on("change", this._onStripeChange)
    this.refreshCta()
  }

  refreshCta() {
    if (!this.hasSubmitBtnTarget) return
    // Toggles the .is-complete class on the submit button so CSS swaps the
    // "Continuer" hint span for the priced "Je m'abonne · X €" span. The
    // form itself drives validation via HTML5 required attributes — we just
    // visualize the combined state. Hidden inputs are never required, so
    // checkValidity() reflects what the user sees.
    const formValid = this.element.checkValidity()
    const complete = Boolean(formValid && this._stripeComplete)
    this.submitBtnTarget.classList.toggle("is-complete", complete)
  }

  observeAmount() {
    if (!this.hasAmountSyncTarget) return
    // Tear down any prior observer before re-attaching — turbo_stream.replace
    // swaps the target node, so each reconnect must point at the current one
    // without leaking the previous observer.
    this.disconnectObserver()
    this._observer = new MutationObserver(() => this.syncAmount())
    this._observer.observe(this.amountSyncTarget, { attributes: true, attributeFilter: ["data-amount-cents"] })
  }

  disconnectObserver() {
    if (!this._observer) return
    this._observer.disconnect()
    this._observer = null
  }

  amountSyncTargetConnected() {
    // Fired on initial mount and after each turbo_stream.replace. Pull the
    // freshest amount and push it into the Stripe Element, then re-observe
    // the new node.
    this.syncAmount()
    this.observeAmount()
  }

  amountSyncTargetDisconnected() {
    // Fired when the target node is removed (e.g. before turbo_stream.replace
    // inserts the new one). Drop the observer so it does not retain the dead
    // node.
    this.disconnectObserver()
  }

  syncAmount() {
    if (!this.elements || !this.hasAmountSyncTarget) return
    const cents = Number(this.amountSyncTarget.dataset.amountCents)
    if (!Number.isFinite(cents) || cents <= 0) return
    if (cents === this.amountValue) return
    this.amountValue = cents
    try {
      this.elements.update({ amount: cents })
    } catch (_) { /* Stripe may reject during a confirm; harmless */ }
  }

  async submit(event) {
    // Only intercept the real "Je m'abonne" submit, not auto-submits.
    const submitter = event.submitter
    const ourBtn = this.hasSubmitBtnTarget ? this.submitBtnTarget : null
    if (!submitter || (ourBtn && submitter !== ourBtn)) return

    event.preventDefault()
    if (!this.elements) {
      this.showError("Le module de paiement n'est pas chargé.")
      return
    }
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true
    this.showOverlay()
    this.clearError()

    // 1) Validate the card field locally and lock the chosen payment method.
    const submitResult = await this.elements.submit()
    if (submitResult.error) {
      this.showError(submitResult.error.message)
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
      this.hideOverlay()
      return
    }

    // 2) Ask the server to create the Stripe Subscription with the freshest
    // totals and return the PaymentIntent's client_secret.
    let clientSecret
    try {
      // Strip Rails' hidden _method input so Rack::MethodOverride doesn't
      // turn this POST into a PATCH (the form itself has method="patch").
      const formData = new FormData(this.element)
      formData.delete("_method")

      const resp = await fetch(this.prepareUrlValue, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken()
        },
        body: formData
      })
      const payload = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        const fallback = "Impossible de préparer le paiement. Réessayez ou contactez le support."
        this.showError(payload.error || fallback)
        if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
        this.hideOverlay()
        return
      }
      clientSecret = payload.client_secret
    } catch (_) {
      this.showError("Erreur réseau. Vérifiez votre connexion.")
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
      this.hideOverlay()
      return
    }

    // 3) Confirm — Element keeps the card data the user typed (no remount).
    const result = await this.stripe.confirmPayment({
      elements: this.elements,
      clientSecret,
      confirmParams: { return_url: this.returnUrlValue }
    })

    if (result.error) {
      this.showError(result.error.message)
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
      this.hideOverlay()
    }
  }

  csrfToken() {
    const meta = document.querySelector("meta[name='csrf-token']")
    return meta ? meta.content : ""
  }

  showOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.add("visible")
  }

  hideOverlay() {
    if (this.hasOverlayTarget) this.overlayTarget.classList.remove("visible")
  }

  showError(message) {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = message
    this.errorMessageTarget.hidden = false
  }

  clearError() {
    if (!this.hasErrorMessageTarget) return
    this.errorMessageTarget.textContent = ""
    this.errorMessageTarget.hidden = true
  }

  appearance() {
    // Match the BD address form: 14.5px on desktop, 16px on mobile
    // (iOS Safari forces zoom below 16). Done at element-create time
    // so Stripe paints with the right size from the first frame.
    const isMobile = window.matchMedia && window.matchMedia("(max-width: 640px)").matches
    const inputSize = isMobile ? "16px" : "14.5px"
    return {
      theme: "flat",
      variables: {
        fontFamily: "'DM Sans', sans-serif",
        fontSizeBase: inputSize,
        colorPrimary: "#D96B55",
        colorText: "#1D1D1B",
        colorTextSecondary: "#555",
        colorBackground: "#FFFFFF",
        borderRadius: "4px",
        spacingUnit: "4px",
        spacingGridRow: "12px"
      },
      rules: {
        ".Input": {
          border: "1.5px solid #E0DCD6",
          padding: "10px 14px",
          fontSize: inputSize,
          transition: "border-color 200ms ease"
        },
        ".Input:focus": {
          border: "1.5px solid #D96B55",
          boxShadow: "none"
        },
        ".Label": {
          fontSize: "13px",
          fontWeight: "500",
          color: "#555"
        }
      }
    }
  }
}
